// Page to handle team invitation acceptance from email links

import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTeam } from '../contexts/TeamContext';

const AcceptInvitationPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isAuthenticated, user, isLoading: authLoading } = useAuth();
  const { loadTeams } = useTeam();
  
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [teamName, setTeamName] = useState<string>('');

  const token = searchParams.get('token');

  useEffect(() => {
    console.log('AcceptInvitationPage useEffect triggered:', { 
      token, 
      isAuthenticated, 
      user: user?.email || 'null', 
      authLoading 
    });
    
    if (!token) {
      console.log('No token found, setting error');
      setError('Invalid invitation link - missing token');
      return;
    }

    // Wait for authentication check to complete
    if (authLoading) {
      console.log('Authentication still loading, waiting...');
      return;
    }

    if (!isAuthenticated || !user) {
      console.log('User not authenticated or user data not loaded, redirecting to login');
      // Redirect to login with return URL
      navigate(`/login?redirect=${encodeURIComponent(window.location.pathname + window.location.search)}`);
      return;
    }

    console.log('User authenticated and loaded, auto-accepting invitation');
    // Auto-accept invitation when user is authenticated and loaded
    acceptInvitation();
  }, [token, isAuthenticated, user, authLoading]);

  const acceptInvitation = async () => {
    console.log('acceptInvitation called with token:', token);
    if (!token) return;

    setLoading(true);
    setError(null);

    try {
      console.log('Making API call to accept invitation');
      const response = await fetch('/api/teams/accept-invitation', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({ token })
      });

      console.log('API response status:', response.status);
      const data = await response.json();
      console.log('API response data:', data);

      if (data.success) {
        console.log('Invitation accepted successfully');
        setSuccess(true);
        setTeamName(data.team?.name || 'the team');
        
        // Reload teams to include the new team
        await loadTeams();
        
        // Redirect to main app after a short delay
        setTimeout(() => {
          navigate('/');
        }, 3000);
      } else {
        console.log('Invitation acceptance failed:', data.error);
        setError(data.error || 'Failed to accept invitation');
      }
    } catch (err) {
      console.error('Error accepting invitation:', err);
      setError('Failed to accept invitation. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="max-w-md w-full bg-white rounded-lg shadow-md p-8 text-center">
          <div className="text-red-500 text-6xl mb-4">❌</div>
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Invalid Invitation</h1>
          <p className="text-gray-600 mb-6">This invitation link is invalid or has expired.</p>
          <button
            onClick={() => navigate('/')}
            className="w-full bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 transition-colors"
          >
            Go to AddWise AI
          </button>
        </div>
      </div>
    );
  }

  // Show loading screen while authentication is being checked
  if (authLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="max-w-md w-full bg-white rounded-lg shadow-md p-8 text-center">
          <div className="text-blue-500 text-6xl mb-4">🔐</div>
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Checking Authentication</h1>
          <p className="text-gray-600 mb-6">Please wait while we verify your login status...</p>
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500 mx-auto"></div>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="max-w-md w-full bg-white rounded-lg shadow-md p-8 text-center">
          <div className="text-blue-500 text-6xl mb-4">🔐</div>
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Login Required</h1>
          <p className="text-gray-600 mb-6">Please log in to accept this team invitation.</p>
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500 mx-auto"></div>
        </div>
      </div>
    );
  }

  // Add a manual test state for debugging
  if (!loading && !success && !error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="max-w-md w-full bg-white rounded-lg shadow-md p-8 text-center">
          <div className="text-blue-500 text-6xl mb-4">👥</div>
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Team Invitation</h1>
          <p className="text-gray-600 mb-6">Ready to accept the team invitation.</p>
          <div className="space-y-3">
            <button
              onClick={acceptInvitation}
              className="w-full bg-green-600 text-white py-2 px-4 rounded-md hover:bg-green-700 transition-colors"
            >
              Accept Invitation Manually
            </button>
            <p className="text-sm text-gray-500">Debug: Token = {token}</p>
            <p className="text-sm text-gray-500">Debug: User = {user?.email}</p>
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="max-w-md w-full bg-white rounded-lg shadow-md p-8 text-center">
          <div className="text-blue-500 text-6xl mb-4">👥</div>
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Accepting Invitation</h1>
          <p className="text-gray-600 mb-6">Please wait while we add you to the team...</p>
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500 mx-auto"></div>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="max-w-md w-full bg-white rounded-lg shadow-md p-8 text-center">
          <div className="text-green-500 text-6xl mb-4">✅</div>
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Welcome to {teamName}!</h1>
          <p className="text-gray-600 mb-6">
            You've successfully joined the team. You can now collaborate on transcripts and share insights.
          </p>
          <div className="space-y-3">
            <button
              onClick={() => navigate('/')}
              className="w-full bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 transition-colors"
            >
              Go to AddWise AI
            </button>
            <p className="text-sm text-gray-500">Redirecting automatically in a few seconds...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="max-w-md w-full bg-white rounded-lg shadow-md p-8 text-center">
          <div className="text-red-500 text-6xl mb-4">❌</div>
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Invitation Failed</h1>
          <p className="text-gray-600 mb-6">{error}</p>
          <div className="space-y-3">
            <button
              onClick={acceptInvitation}
              disabled={loading}
              className="w-full bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50"
            >
              Try Again
            </button>
            <button
              onClick={() => navigate('/')}
              className="w-full bg-gray-100 text-gray-700 py-2 px-4 rounded-md hover:bg-gray-200 transition-colors"
            >
              Go to AddWise AI
            </button>
          </div>
        </div>
      </div>
    );
  }

  return null;
};

export default AcceptInvitationPage; 